import { AnimatePresence, motion } from 'framer-motion';
import { TextAreaField } from '~/components/shared/forms';
import { useCallback, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Divider,
  InputSelect,
  Label,
  MultiSelectGroup,
  NumberField,
} from '~/commonComponents';
import { benefitsOptions, benefitsOtherMaxLength } from '~/components/PermanentJobsV2/utils';
import { CompensationType, PayType } from '~/domain/PermanentJobs/enums';

const PAY_TYPE_OPTIONS = [
  { title: 'per year', value: PayType.SALARY },
  { title: 'per hour', value: PayType.HOURLY },
  { title: 'per day', value: PayType.DAILY },
];

export const EarningBenefitForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const watchBenefits = useWatch({ control, name: 'benefits' });
  const watchMaxRate = useWatch({ control, name: 'maxRate' });

  // Auto-set compensationType based on maxRate (an empty max means a fixed amount)
  useEffect(() => {
    if (watchMaxRate) {
      setValue('compensationType', CompensationType.RANGE);
    } else {
      setValue('compensationType', CompensationType.FIXED);
    }
  }, [watchMaxRate, setValue]);

  const handleBenefitsChange = useCallback(
    (selectedValues: string[], fieldOnChange: (...event: any[]) => void) => {
      if (!selectedValues.includes('Other')) {
        setValue?.('benefitsOther', '');
      }

      fieldOnChange(selectedValues);
    },
    [setValue],
  );

  return (
    <Column gap={6}>
      <Column gap={3}>
        <Label text="Stated Pay" />
        <Row align="center" gap={5}>
          <Column>
            <Label text="Minimum Pay" />
            <Row align="center" gap={2}>
              <Typography as="span" bold variant="body1">
                $
              </Typography>
              <Controller
                control={control}
                name="minRate"
                render={({ field }) => (
                  <NumberField
                    placeholder="Enter Minimum"
                    value={field.value}
                    setValue={field.onChange}
                    currency
                    customInputStyle={{ width: '140px' }}
                    error={!!errors?.minRate}
                  />
                )}
              />
            </Row>
          </Column>
          <Typography as="span" bold variant="body1" style={{ marginTop: 28 }}>
            —
          </Typography>
          <Column>
            <Label text="Maximum Pay" required={false} />
            <Row align="center" gap={2}>
              <Typography as="span" bold variant="body1">
                $
              </Typography>
              <Controller
                control={control}
                name="maxRate"
                render={({ field }) => (
                  <NumberField
                    placeholder="Enter Maximum"
                    value={field.value}
                    setValue={field.onChange}
                    currency
                    customInputStyle={{ width: '140px' }}
                    error={!!errors?.maxRate}
                  />
                )}
              />
            </Row>
          </Column>
          <Column>
            <Label text="Pay Type" />
            <Controller
              control={control}
              name="payType"
              render={({ field }) => (
                <Box width="180px">
                  <InputSelect
                    placeholder="Pay type"
                    value={field.value}
                    setValue={field.onChange}
                    isSelectVisible={false}
                    options={PAY_TYPE_OPTIONS}
                    dataCy="perm-pay-type-select"
                  />
                </Box>
              )}
            />
          </Column>
        </Row>
        <Typography italic color={colors.neutral[500]}>
          Leave maximum pay empty if you offer a fixed amount.
        </Typography>
      </Column>

      <Divider height={1} marginVertical={0} />

      <Column gap={3}>
        <Label text="Benefits" required={false} />
        <Typography italic color={colors.neutral[500]}>
          <Controller
            control={control}
            name="benefits"
            render={({ field }) => (
              <MultiSelectGroup
                value={field.value}
                variant="pill"
                options={benefitsOptions.map((option) => ({
                  value: option.code,
                  label: option.name,
                }))}
                onChange={(selected) =>
                  handleBenefitsChange(
                    selected.map((s) => s.value),
                    field.onChange,
                  )
                }
              />
            )}
          />
        </Typography>
        <AnimatePresence mode="wait">
          {watchBenefits?.includes('Other') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Column gap={3} className="mt-2">
                <Typography as="span">Enter any other benefits below:</Typography>
                <Controller
                  control={control}
                  name="benefitsOther"
                  render={({ field }) => (
                    <TextAreaField
                      {...field}
                      height={60}
                      maxLength={benefitsOtherMaxLength}
                      placeholder="Enter other benefits here"
                    />
                  )}
                />
              </Column>
            </motion.div>
          )}
        </AnimatePresence>
      </Column>
    </Column>
  );
};
